import { openDB, IDBPDatabase } from "idb";
import { indexed_db_entities, app_version, env } from "../domain/config";
import StoreInterfaceClass from "./store-interface";

const DB_NAME = `sentinel_solar_${env}`;
let dbInstance: IDBPDatabase | null = null;

async function getDB(): Promise<IDBPDatabase> {
	if (dbInstance) return dbInstance;

	const parsedVersion = parseInt(String(app_version), 10);
	const dbVersion =
		!isNaN(parsedVersion) && parsedVersion > 0 ? parsedVersion : 1;

	dbInstance = await openDB(DB_NAME, dbVersion, {
		upgrade(db) {
			for (const key in indexed_db_entities) {
				const storeName = (indexed_db_entities as Record<string, string>)[key];
				if (!db.objectStoreNames.contains(storeName)) {
					db.createObjectStore(storeName);
				}
			}
		},
		blocked() {
			console.warn("IndexedDB upgrade blocked by another open connection.");
		},
		blocking() {
			console.warn(
				"A newer version of the database is ready. Please close other tabs."
			);
		},
		terminated() {
			console.error("IndexedDB connection unexpectedly closed.");
		}
	});

	return dbInstance;
}

export default class IndexedDbInterfaceClass {
	private entity_name: string;
	private local_storage_interface: StoreInterfaceClass;

	constructor(entity_name: string) {
		this.entity_name = entity_name;
		this.local_storage_interface = new StoreInterfaceClass();
	}

	/**
	 * Replaces entity data inside IndexedDB transactionally and registers expiry metadata.
	 */
	public async set(
		data: any[],
		index_prop: string,
		expiration_date: Date | string | number | null = null
	): Promise<void> {
		const db = await getDB();
		await this.clear_entity();

		this.local_storage_interface.set(
			this.entity_name,
			{
				has_data: true,
				expiration_date,
				data: true
			},
			expiration_date
		);

		const tx = db.transaction(this.entity_name, "readwrite");
		for (const item of data) {
			await tx.store.add(item, item[index_prop]);
		}
		await tx.done;
	}

	/**
	 * Creates or updates a single record inside active ObjectStore.
	 */
	public async put(data: any, index_value: string | number): Promise<void> {
		const db = await getDB();
		const tx = db.transaction(this.entity_name, "readwrite");
		await tx.store.put(data, index_value);
		await tx.done;
	}

	/**
	 * Reads all records or a single item matching the provided primary key index.
	 */
	public async get<T = any>(index: string | number): Promise<T | null>;
	public async get<T = any>(index?: null): Promise<T[] | null>;
	public async get<T = any>(
		index: string | number | null = null
	): Promise<T | T[] | null> {
		const db = await getDB();
		const entity_info = this.local_storage_interface.get<any>(this.entity_name);
		if (!entity_info) return null;

		const tx = db.transaction(this.entity_name);
		const store = tx.store;

		if (index !== null) {
			const result = await store.get(index);
			return (result as T) || null;
		} else {
			const all = await store.getAll();
			return all.length > 0 ? (all as T[]) : null;
		}
	}

	/**
	 * Deletes a single key entry from ObjectStore.
	 */
	public async delete(index: string | number): Promise<void> {
		const db = await getDB();
		const tx = db.transaction(this.entity_name, "readwrite");
		await tx.store.delete(index);
		await tx.done;
	}

	/**
	 * Clears ObjectStore data and removes expiry metadata flag.
	 */
	public async clear_entity(): Promise<void> {
		const db = await getDB();
		const entity_info = this.local_storage_interface.get<any>(this.entity_name);
		if (!entity_info || !entity_info.has_data) return;

		await db.clear(this.entity_name);
		this.local_storage_interface.delete_entity_values(this.entity_name);
	}

	/**
	 * Clears all defined IndexedDB stores.
	 */
	public async delete_all_entities(): Promise<void> {
		const db = await getDB();
		const storeNames = Object.values(indexed_db_entities) as string[];
		const tx = db.transaction(storeNames, "readwrite");

		for (const storeName of storeNames) {
			await tx.objectStore(storeName).clear();
		}

		await tx.done;
	}

	/**
	 * Removes entire database instance if supported by current environment.
	 */
	public async remove_db(): Promise<void> {
		if (
			env !== "test" &&
			typeof window !== "undefined" &&
			typeof window.indexedDB?.databases === "function"
		) {
			const databases = await window.indexedDB.databases();
			const exists = databases.some((db) => db.name === DB_NAME);
			if (exists) {
				window.indexedDB.deleteDatabase(DB_NAME);
				dbInstance = null;
			}
		}
	}
}
